<!--
  Top bar: back (left), object type (read-only), name, settings and save for Passport Builder v2.
-->
<script setup lang="ts">
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { WObjectTypeSearch } from '@/widgets'
import { VIcons } from '@/shared/ui'

const props = defineProps<{
  formBase: Record<string, any> | null
  isSaving: boolean
  isDirty: boolean
}>()

defineEmits<{
  save: []
  'open-settings': []
}>()

const route = useRoute()
const router = useRouter()

const backLink = computed(() =>
  router.resolve({
    name: 'objects.groups.admin',
    params: {
      ...route.params,
      action: null,
      entity_id: null,
    },
  }),
)

const parseIsSystemValue = (value: unknown): boolean | null => {
  if (value === undefined || value === null || value === '') return null
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return value === 1

  const normalized = String(value).toLowerCase()
  if (normalized === '1' || normalized === 'true') return true
  if (normalized === '0' || normalized === 'false') return false

  return Boolean(value)
}

/** Match {@link InventObjectTypesForm} filtering for passports-config object type picker. */
const objectTypeIsSystem = computed<boolean | null>(() => {
  if (!props.formBase?.object_type) return null
  return parseIsSystemValue(props.formBase.object_type.is_system)
})
</script>

<template>
  <header class="pb-header" :class="{ 'pb-header--dirty': isDirty }">
    <VBtn
      :to="backLink"
      icon
      variant="text"
      density="comfortable"
      class="pb-header-back flex-shrink-0"
      :aria-label="$t('itf.back_list')"
    >
      <VIcons type="arrow_back" />
    </VBtn>
    <template v-if="formBase">
      <WObjectTypeSearch
        v-if="formBase.object_type"
        :label="$t('itf.elements')"
        placeholder=" "
        :multiple="false"
        :is-system="objectTypeIsSystem"
        :disabled="true"
        hide-details
        v-model="formBase.object_type.id"
        class="pb-header-object-type"
      />
      <VTextField
        v-model="formBase.name"
        :label="$t('itf.name_label')"
        placeholder=" "
        variant="outlined"
        density="compact"
        hide-details="auto"
        class="pb-header-name"
      />
    </template>
    <div v-else class="pb-header__skeleton d-flex gap-4 flex-grow-1 align-center">
      <VSkeletonLoader type="heading" width="200" />
      <VSkeletonLoader type="heading" width="280" />
    </div>

    <div class="pb-header-spacer" />

    <div class="pb-header-actions">
      <VBtn
        v-if="formBase"
        icon
        variant="text"
        density="comfortable"
        class="pb-header-settings"
        :aria-label="$t('itf.grid_settings')"
        @click="$emit('open-settings')"
      >
        <VIcons type="settings" />
      </VBtn>
      <VBtn
        icon
        variant="text"
        density="comfortable"
        color="primary"
        :loading="isSaving"
        :aria-label="$t('itf.save')"
        @click="$emit('save')"
      >
        <VIcons type="save" />
      </VBtn>
    </div>
  </header>
</template>

<style scoped lang="scss">
.pb-header-object-type {
  min-width: 200px;
  max-width: 280px;
}

.pb-header-name {
  min-width: 220px;
  max-width: 360px;
}

.pb-header--dirty {
  box-shadow: inset 0 -2px 0 0 rgba(25, 118, 210, 0.35);
}
</style>
